// Cabinet Studio data layer — thin wrapper over `ddb_wasm_client.js`.
//
// Loads the wasm module once, opens a single Client per session,
// keeps Collection handles cached, and re-exports a façade that
// matches the original REST/WS-based API (`coll(name).find/query/…`,
// `subscribe(coll, conds, handler)`, `login/logout`, `vfs(db).…`).
// Consumers (hooks, panels, pages) didn't need to change.
//
// The wasm bundle ships embind handles whose C++ memory is freed via
// `.delete()`. We pin Collection / EventBus / VfsManager handles
// for the life of the connection and release them in `logout()`.
// Transient call-scoped handles (Subscription) are released by the
// returned RAII wrappers.

import type {
  ChangeEvent,
  Condition,
  Id,
  QueryOptions,
  Role,
  WhoAmI,
} from "./types";
import type {
  Client,
  Collection,
  Condition as WasmCondition,
  DdbValue,
  EventBus,
  Subscription as WasmSubscription,
  VfsManager,
} from "./ddb_wasm_client";

// ── Persisted creds (so reloads can re-attach) ───────────────────

const USER_KEY   = "ddb.user";
const PASS_KEY   = "ddb.pass";        // SPA-only; not security-sensitive in dev
const WHOAMI_KEY = "ddb.whoami";

interface StoredCreds { user: string; password: string; }

function loadCreds(): StoredCreds | null {
  const u = localStorage.getItem(USER_KEY);
  const p = localStorage.getItem(PASS_KEY);
  if (!u || !p) return null;
  return { user: u, password: p };
}
function saveCreds(c: StoredCreds | null) {
  if (!c) { localStorage.removeItem(USER_KEY); localStorage.removeItem(PASS_KEY); return; }
  localStorage.setItem(USER_KEY, c.user);
  localStorage.setItem(PASS_KEY, c.password);
}

/** Legacy export — App's `RequireAuth` calls `getToken()` to gate routes.
 *  We have no token anymore; a saved cred pair stands in. */
export function getToken(): string | null {
  return loadCreds() ? "wasm" : null;
}

export function getCachedWhoAmI(): WhoAmI | null {
  const s = localStorage.getItem(WHOAMI_KEY);
  if (!s) return null;
  try { return JSON.parse(s) as WhoAmI; } catch { return null; }
}
function setCachedWhoAmI(w: WhoAmI | null) {
  if (w === null) localStorage.removeItem(WHOAMI_KEY);
  else localStorage.setItem(WHOAMI_KEY, JSON.stringify(w));
}

// ── Errors ────────────────────────────────────────────────────────

/** Kept so existing `catch (e instanceof DdbHttpError)` sites still
 *  compile.  We don't throw it anymore — wasm methods throw plain Error. */
export class DdbHttpError extends Error {
  constructor(public status: number, public body: string) {
    super(`ddb ${status}: ${body}`);
    this.name = "DdbHttpError";
  }
}

// ── Module + Client bootstrap ────────────────────────────────────

function wsUrl(): string {
  // In dev we hit ddb_server's WS directly to avoid vite's proxy quirks.
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  if (import.meta.env.DEV) {
    const base = import.meta.env.VITE_DDB_BASE as string | undefined;
    if (base) {
      const u = new URL(base);
      return `${u.protocol === "https:" ? "wss:" : "ws:"}//${u.host}`;
    }
    return `${proto}//${location.hostname}:8080`;
  }
  // Prod: nginx vhost proxies /ws (and /api/, /db/) to 127.0.0.1:8081
  // while serving the static SPA from /. Without the /ws suffix the WS
  // upgrade lands on the static block and gets answered with index.html.
  return `${proto}//${location.host}/ws`;
}

let _client: Client | null = null;
let _clientPromise: Promise<Client> | null = null;
const _collCache = new Map<string, Collection>();
let _eventBus: EventBus | null = null;
let _vfs: VfsManager | null = null;
let _connectFailed = false;

// Emscripten Asyncify allows only ONE pending suspension per wasm instance:
// any second embind call that hits an internal `emscripten_sleep` while
// another is in flight traps with `Aborted(RuntimeError: unreachable)`,
// after which the whole module is poisoned. React mounts useQuery /
// useSubscription hooks in parallel, so we serialize every wasm-boundary
// call behind a global Promise chain. Each `wasmCall` runs only once its
// predecessor has settled (success OR failure).
let _wasmModule: { getExceptionMessage?: (ptr: number) => string[] } | null = null;
let _wasmQueue: Promise<unknown> = Promise.resolve();

// Once the wasm module hits a runtime trap (memory access out of bounds,
// `unreachable`, division by zero, …) the whole instance is poisoned —
// every subsequent embind call traps the same way. Detect those messages
// and drop our cached client so the next call rebuilds from scratch via
// `getClient()`.
function isWasmTrap(e: unknown): boolean {
  if (e instanceof Error) {
    const m = e.message;
    return m.includes("memory access out of bounds")
        || m.includes("RuntimeError: unreachable")
        || m.includes("table index is out of bounds")
        || m.includes("integer divide by zero")
        || m.includes("indirect call signature mismatch");
  }
  return false;
}
function poisonClient() {
  // Tear down so the next wasmCall re-bootstraps. We can't `delete()` a
  // trapped client — its destructor would itself trap. Just drop refs and
  // let the GC eat them once nothing's holding on.
  _collCache.clear();
  _eventBus = null;
  _vfs = null;
  _client = null;
  _clientPromise = null;
}

function wasmCall<T>(fn: () => Promise<T>): Promise<T> {
  // Emscripten throws C++ exceptions as raw integer pointers ("6373728"
  // style). Convert any non-Error throw into a real Error so React's
  // error boundaries and toast catches see a useful `.message`. When
  // available, decode the C++ message via Module.getExceptionMessage —
  // otherwise fall back to the bare pointer text. Poison the client
  // on WebAssembly runtime traps (memory OOB / unreachable / …).
  const wrapped = () => fn().catch((e) => {
    if (typeof e === "number") {
      let err: Error;
      if (_wasmModule?.getExceptionMessage) {
        try {
          const [type, msg] = _wasmModule.getExceptionMessage(e);
          err = new Error(msg || type || `wasm exception (ptr ${e})`);
          err.name = type || "WasmException";
        } catch {
          err = new Error(`wasm exception (ptr ${e})`);
        }
      } else {
        err = new Error(`wasm exception (ptr ${e})`);
      }
      if (isWasmTrap(err)) poisonClient();
      throw err;
    }
    if (isWasmTrap(e)) poisonClient();
    throw e;
  });
  const next = _wasmQueue.then(wrapped, wrapped);
  _wasmQueue = next.then(() => undefined, () => undefined);
  return next as Promise<T>;
}

/** Lazy bootstrap. Connects and logs in using saved creds. Throws on auth
 *  failure so RequireAuth can redirect to /login. */
async function getClient(): Promise<Client> {
  if (_client) return _client;
  if (_clientPromise) return _clientPromise;
  const creds = loadCreds();
  if (!creds) throw new Error("not logged in");
  _clientPromise = (async () => {
    const Module = await DdbClient();
    _wasmModule = Module as typeof _wasmModule;
    const c = new Module.Client();
    const cs = await c.connect(wsUrl(), 0);
    if (!cs.ok) { c.delete(); _clientPromise = null; _connectFailed = true; throw new Error(cs.error); }
    const ls = await c.login(creds.user, creds.password);
    if (!ls.ok) {
      c.disconnect(); c.delete();
      _clientPromise = null;
      saveCreds(null);
      setCachedWhoAmI(null);
      throw new Error(ls.error);
    }
    _client = c;
    _connectFailed = false;
    return c;
  })();
  return _clientPromise;
}

void _connectFailed;       // reserved for future "show offline banner" UX

// ── Auth ──────────────────────────────────────────────────────────

export async function login(user: string, password: string): Promise<WhoAmI> {
  // Tear down any half-open client so the next getClient() picks up new creds.
  await teardown();
  saveCreds({ user, password });
  const client = await getClient();
  void client;
  // Best-effort role inference.
  const isAdmin = user === "dba";
  const role: Role = isAdmin ? "admin" : "customer";
  const who: WhoAmI = { username: user, groups: [], role, isAdmin };
  setCachedWhoAmI(who);
  return who;
}

export async function logout(): Promise<void> {
  await teardown();
  saveCreds(null);
  setCachedWhoAmI(null);
}

async function teardown(): Promise<void> {
  for (const [, coll] of _collCache) {
    try { coll.delete(); } catch { /* ignore */ }
  }
  _collCache.clear();
  if (_eventBus) { try { _eventBus.delete(); } catch { /* ignore */ } _eventBus = null; }
  if (_vfs) { try { _vfs.delete(); } catch { /* ignore */ } _vfs = null; }
  const c = _client;
  _client = null;
  _clientPromise = null;
  if (c) {
    try { c.disconnect(); } catch { /* ignore */ }
    try { c.delete(); } catch { /* ignore */ }
  }
}

// ── Collection cache ─────────────────────────────────────────────

async function getColl(name: string): Promise<Collection> {
  const cached = _collCache.get(name);
  if (cached) return cached;
  const client = await getClient();
  const c = client.collection(name);
  _collCache.set(name, c);
  return c;
}

function toWasmCond(c: Condition): WasmCondition {
  return { field: c.field, op: c.op, value: c.value as DdbValue };
}

// ── Public CRUD facade ───────────────────────────────────────────

export const coll = (name: string) => ({
  find<T>(id: Id): Promise<T | null> {
    return wasmCall(async () => {
      const c = await getColl(name);
      const row = await c.find(id);
      return (row as unknown as T | null) ?? null;
    });
  },

  all<T>(opts: QueryOptions = {}): Promise<T[]> {
    if (opts.sort != null || opts.limit != null || opts.skip != null)
      return this.query<T>([], opts);
    return wasmCall(async () => {
      const c = await getColl(name);
      const rows = await c.all();
      return rows as unknown as T[];
    });
  },

  query<T>(conds: Condition[], opts: QueryOptions = {}): Promise<T[]> {
    return wasmCall(async () => {
      const c = await getColl(name);
      const wasmOpts: { orderBy?: string; descending?: boolean; limit?: number; offset?: number } = {};
      if (opts.sort) {
        if (opts.sort.startsWith("-")) {
          wasmOpts.orderBy = opts.sort.slice(1);
          wasmOpts.descending = true;
        } else {
          wasmOpts.orderBy = opts.sort;
        }
      }
      if (opts.limit  != null) wasmOpts.limit  = opts.limit;
      if (opts.skip   != null) wasmOpts.offset = opts.skip;
      const rows = await c.query(conds.map(toWasmCond), wasmOpts);
      return rows as unknown as T[];
    });
  },

  count(conds?: Condition[]): Promise<number> {
    return wasmCall(async () => {
      const c = await getColl(name);
      if (!conds || conds.length === 0) return c.count();
      const rows = await c.query(conds.map(toWasmCond));
      return rows.length;
    });
  },

  create<T>(fields: Partial<T>): Promise<Id> {
    return wasmCall(async () => {
      const c = await getColl(name);
      const row = await c.create(fields as { [k: string]: DdbValue });
      return row._id;
    });
  },

  update<T>(id: Id, changes: Partial<T>): Promise<void> {
    return wasmCall(async () => {
      const c = await getColl(name);
      const r = await c.update(id, changes as { [k: string]: DdbValue });
      if (!r.ok) throw new Error(r.error);
    });
  },

  remove(id: Id): Promise<void> {
    return wasmCall(async () => {
      const c = await getColl(name);
      const r = await c.remove(id);
      if (!r.ok) throw new Error(r.error);
    });
  },

  batchCreate<T>(rows: Partial<T>[]): Promise<number> {
    return wasmCall(async () => {
      const c = await getColl(name);
      const r = await c.batchCreate(rows as { [k: string]: DdbValue }[]);
      return r.length;
    });
  },
});

// ── Scripts ──────────────────────────────────────────────────────

export function runScript(
  source: string,
  lang: "javascript" | "python" | "vbscript" = "javascript",
): Promise<{ ok: boolean; output: string[]; message: string }> {
  return wasmCall(async () => {
    const client = await getClient();
    const sm = client.scripts();
    try {
      const r = await sm.runSource(source, lang);
      return { ok: r.ok, output: r.output, message: r.message };
    } finally {
      try { sm.delete(); } catch { /* ignore */ }
    }
  });
}

/** Invoke `Cabinet.method(args...)` on a single instance by id. */
export async function callCabinetMethod(
  classId: string,
  instanceId: Id,
  method: string,
  args: unknown[] = [],
): Promise<unknown> {
  const argsLit = JSON.stringify(args);
  const src = `
    var c = db.cabinets.find(${instanceId});
    if (!c) { return null; }
    var inst = new ${classId}(c);
    var r = inst.${method}.apply(inst, ${argsLit});
    return r;
  `;
  const r = await runScript(src, "javascript");
  if (!r.ok) throw new Error(r.message);
  return r.output;
}

// ── VFS ──────────────────────────────────────────────────────────

export const vfs = (_db = "default") => {
  // wasm Client is single-DB per session; the `db` arg is preserved for
  // call-site compatibility but ignored. Switching DBs would go through
  // `client.server().useDb()` — out of scope here.
  return {
    read(path: string): Promise<Blob> {
      return wasmCall(async () => {
        const v = await getVfs();
        const res = await v.read(path) as { content?: string; blob?: Uint8Array };
        if (res.blob && res.blob.length > 0) return new Blob([res.blob as BlobPart]);
        return new Blob([res.content ?? ""], { type: "text/plain" });
      });
    },
    async write(path: string, body: Blob | string | ArrayBuffer): Promise<void> {
      let payload: { text: string } | { blob: Uint8Array };
      if (typeof body === "string") {
        payload = { text: body };
      } else if (body instanceof ArrayBuffer) {
        payload = { blob: new Uint8Array(body) };
      } else {
        const buf = new Uint8Array(await body.arrayBuffer());
        const isText = body.type.startsWith("text/") || body.type.includes("json");
        payload = isText ? { text: new TextDecoder().decode(buf) } : { blob: buf };
      }
      return wasmCall(async () => {
        const v = await getVfs();
        if ("text" in payload) await v.write(path, payload.text);
        else                   await v.writeBlob(path, payload.blob);
      });
    },
    del(path: string): Promise<void> {
      return wasmCall(async () => {
        const v = await getVfs();
        const r = await v.del(path, false);
        if (!r.ok) throw new Error(r.error);
      });
    },
  };
};

async function getVfs(): Promise<VfsManager> {
  if (_vfs) return _vfs;
  const client = await getClient();
  _vfs = client.vfs();
  return _vfs;
}

// ── Server info / health ──────────────────────────────────────────

export function health(): Promise<boolean> {
  return wasmCall(async () => {
    try {
      const client = await getClient();
      const s = client.server();
      try {
        const r = await s.ping();
        return r.ok;
      } finally {
        try { s.delete(); } catch { /* ignore */ }
      }
    } catch { return false; }
  });
}

export function serverInfo(): Promise<Record<string, unknown>> {
  return wasmCall(async () => {
    const client = await getClient();
    const s = client.server();
    try { return (await s.serverInfo()) as Record<string, unknown>; }
    finally { try { s.delete(); } catch { /* ignore */ } }
  });
}

// ── Subscription ──────────────────────────────────────────────────

export interface Subscription {
  close(): void;
  isActive(): boolean;
}

async function getEventBus(): Promise<EventBus> {
  if (_eventBus) return _eventBus;
  const client = await getClient();
  _eventBus = client.events();
  return _eventBus;
}

/** Open a live subscription on `collection`. Conditions are forwarded
 *  through `EventBus.subscribeWhere(...)` — the server pre-filters by
 *  every condition (AND) before pushing an EVENT frame, and the
 *  EventDispatcher in the client re-applies the same predicate so
 *  multiple subs on the same (coll,type) with different conds each
 *  receive only their matches. AfterDelete frames carry an empty
 *  FieldMap and bypass the filter — track ids in the handler if you
 *  need 1:1 list bookkeeping. */
export function subscribe<T extends { _id: Id }>(
  collection: string,
  conds: Condition[],
  handler: (e: ChangeEvent<T>) => void,
): Subscription {
  let active = true;
  const wasmSubs: WasmSubscription[] = [];

  const kindFromType = (n: number): ChangeEvent["kind"] | null => {
    switch (n) {
      case 0: return "after_insert";
      case 1: return "after_update";
      case 2: return "after_delete";
      default: return null;
    }
  };

  // Condition shape from "./types": { field, op, value }. The wasm
  // embind binding accepts `{ field, op, value | val }` — both keys
  // work — and `op` can be the CmpOp enum int OR the string name.
  // Forward the SPA's lowercase op names ("eq"/"ne"/…) verbatim.
  const wasmConds = conds.map((c) => ({
    field: c.field,
    op:    c.op,
    value: c.value,
  }));

  // Pick the most specific subscribe flavour the wasm bus offers (see
  // wasm-client-guide.md §8). The dedicated `subscribeObject(collection,
  // id, type, cb)` path is what powers the project doc's single-row
  // watch (the SPA's only subscription target since the nested-hierarchy
  // refactor); the server pushes only matching events instead of
  // streaming the whole collection. Falls through to subscribeWhere /
  // subscribe for multi-row or unfiltered cases.
  const singleIdEq =
    wasmConds.length === 1
    && wasmConds[0]!.field === "_id"
    && wasmConds[0]!.op === "eq"
    && typeof wasmConds[0]!.value === "number"
      ? (wasmConds[0]!.value as number)
      : null;

  // Every embind subscribe is Asyncified — two concurrent useSubscription
  // hooks on the same page would both call bus.subscribe() and trap.
  // Funnel through the global wasm queue so each `subscribe()` invocation
  // serializes its 3 evType subscribes behind any other wasm work.
  void wasmCall(async () => {
    const bus = await getEventBus();
    if (!active) return;
    // Pass numeric literals (0/1/2) for EventType — wasm `jsToEventType`
    // checks `typeof === "number"`.
    const types: number[] = [0, 1, 2];
    const onEvent = (ev: { type: number; objectId: Id; fields: unknown }) => {
      if (!active) return;
      const kind = kindFromType(ev.type);
      if (!kind) return;
      handler({ collection, kind, objectId: ev.objectId,
                fields: ev.fields as Partial<T> });
    };
    const hasSubscribeObject = typeof (bus as unknown as {
      subscribeObject?: typeof bus.subscribe;
    }).subscribeObject === "function";
    const hasSubscribeWhere = typeof (bus as unknown as {
      subscribeWhere?: typeof bus.subscribe;
    }).subscribeWhere === "function";
    for (const evType of types) {
      const sub = singleIdEq != null && hasSubscribeObject
        ? await bus.subscribeObject(collection, singleIdEq, evType, onEvent)
        : wasmConds.length > 0 && hasSubscribeWhere
        ? await bus.subscribeWhere(collection, evType, wasmConds, onEvent)
        : await bus.subscribe(collection, evType, onEvent);
      if (!active) {
        try { sub.release(); } catch { /* ignore */ }
        try { sub.delete();  } catch { /* ignore */ }
        return;
      }
      wasmSubs.push(sub);
    }
  }).catch((err) => console.warn("subscribe failed", err));

  return {
    close() {
      if (!active) return;
      active = false;
      for (const sub of wasmSubs) {
        try { sub.release(); } catch { /* ignore */ }
        try { sub.delete();  } catch { /* ignore */ }
      }
      wasmSubs.length = 0;
    },
    isActive() { return active; },
  };
}

// Re-export everything callers need from one place.
export type { Condition, QueryOptions, WhoAmI, ChangeEvent } from "./types";
